import Immutable from 'immutable';
import _ from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import ArtboardLayoutContainer from 'components/ArtboardLayoutContainer';
import GroupLayout from 'components/GroupLayout';
import { selectLayoutsUploadingStatusMap } from 'containers/Layouts/selectors';
import { GroupLayoutMap, LayeredCombinedLayoutsMap, LayoutMap } from 'models';
import * as Models from 'models';
import { isGroupLayout } from 'utils/layouts/isGroupLayout';

type Props = {
  documents?: Models.CombinedDocumentsMap;
  getLayoutsRefs?: () => Immutable.Map<string, HTMLDivElement>;
  groupLayoutId?: string;
  isGroupLayoutWithOneChild?: boolean;
  isSectionActive: boolean;
  isReadOnly: boolean;
  layoutIds: Immutable.List<string>;
  layouts: Models.CombinedLayoutsList;
  moveableLayoutId?: string;
  relationsByRegularLayoutId?: Immutable.Map<string, Models.LayeredRelationsMap>;
  saveLayoutRef?: (layoutId: string, element: HTMLDivElement) => void;
  section: Models.ExtendedSectionMap;
  skipFirstLayout?: boolean;
  ssi: Models.SSIMap | null;
  dataTestId: string;
};

const ArtboardLayoutContainerList: React.FunctionComponent<Props> = (props) => {
  const {
    documents,
    getLayoutsRefs,
    groupLayoutId,
    isGroupLayoutWithOneChild,
    isSectionActive,
    layoutIds,
    layouts,
    moveableLayoutId,
    relationsByRegularLayoutId,
    saveLayoutRef = _.noop,
    section,
    skipFirstLayout = false,
    ssi,
    isReadOnly,
    dataTestId,
  } = props;
  const hasOneChildLayout = layouts.size === 1;
  const layoutsIds = layouts.map(layout => (layout as unknown as Models.BaseLayoutMap)?.get('id')).toArray();
  const layoutsUploadingStatusMap = useSelector((state: LayeredCombinedLayoutsMap) => selectLayoutsUploadingStatusMap(state, layoutsIds));

  return (
    <>
      {
        layouts.toArray().map((layout, idx) => {
          if (skipFirstLayout && idx === 0) {
            return null;
          }

          const layoutId = (layout as unknown as Models.BaseLayoutMap).get('id');
          const position = layoutIds.findIndex(id => (layout as unknown as Models.BaseLayoutMap).get('id') === id);
          const isGroup = isGroupLayout(layout);
          const isCarouselItem = !!(layout as unknown as Models.BaseLayoutMap).get('carouselItem');
          if (isGroup) {
            return (
              <GroupLayout
                isCarouselItem={isCarouselItem}
                isLastLayout={hasOneChildLayout}
                isLayoutBeingUploaded={layoutsUploadingStatusMap.get(layoutId) ?? false}
                isReadOnly={isReadOnly}
                getLayoutsRefs={getLayoutsRefs}
                isSectionActive={isSectionActive}
                key={layoutId}
                layout={layout as GroupLayoutMap}
                position={position}
                saveLayoutRef={saveLayoutRef}
                section={section}
                ssi={ssi}
                dataTestId={`${dataTestId}-groupLayout${idx ?? 0}`}
              />
            );
          }

          return (
            <ArtboardLayoutContainer
              disableDragAndDelete={isGroupLayoutWithOneChild}
              documents={documents}
              groupLayoutId={groupLayoutId}
              isFirst={idx === 0}
              isLastLayout={hasOneChildLayout}
              isSectionActive={isSectionActive}
              isReadOnly={isReadOnly}
              key={layoutId}
              layout={layout as LayoutMap}
              layoutId={layoutId}
              layoutRelations={relationsByRegularLayoutId && relationsByRegularLayoutId.get(layoutId)}
              moveableArtboardLayoutId={moveableLayoutId}
              position={position}
              saveLayoutRef={saveLayoutRef}
              sectionDisplayName={section.get('displayName')}
              sectionName={section.get('name')}
              sectionStyles={section.get('brandStyles')}
              ssi={ssi}
              dataTestId={`${dataTestId}-layoutContainer${idx ?? 0}`}
            />
          );

        })
      }
    </>
  );
};

export default ArtboardLayoutContainerList;
